/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { FC, useEffect, useState } from 'react'
import {
  useCreateEmailRecipient,
  useDeleteEmailRecipient,
  useGetEmailAlertRecipients,
  useUpdateEmailRecipient,
} from 'framework/api/methods'

import { dateToReadableString, isEmail } from 'utils'

import { NotificationRecipientCard } from '../EmailAlertsCardList/EmailAlertReportCard'
import { ProcessBadge } from '../ProcessBadge'

import { RecipientRow } from './RecipientRow'

export interface EmailAlertsRecipientsModalProps {
  card: NotificationRecipientCard
  canEdit: boolean
  onClose: () => void
  notify: (message: string, type: 'success' | 'error') => void
  onRecipientCountChanged: () => void
}

export const EmailAlertsRecipientsModal: FC<
  EmailAlertsRecipientsModalProps
> = ({
  card,
  canEdit,
  onClose,
  notify,
  onRecipientCountChanged,
}: EmailAlertsRecipientsModalProps) => {
  const { data, isLoading, refetch } = useGetEmailAlertRecipients(
    card.partnerId,
    card.serviceName,
  )
  const recipients = data?.recipients ?? []

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editError, setEditError] = useState('')
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null,
  )
  const [addValue, setAddValue] = useState('')
  const [addError, setAddError] = useState('')

  const { mutate: createRecipient, isLoading: isCreating } =
    useCreateEmailRecipient()
  const { mutate: updateRecipient } = useUpdateEmailRecipient()
  const { mutate: deleteRecipient } = useDeleteEmailRecipient()

  const resetTransientState = () => {
    setEditingId(null)
    setEditError('')
    setConfirmingDeleteId(null)
    setAddValue('')
    setAddError('')
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (editingId || confirmingDeleteId || addValue) {
        resetTransientState()
      } else {
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, confirmingDeleteId, addValue])

  const refreshAfterChange = () => {
    refetch()
    onRecipientCountChanged()
  }

  const handleAdd = () => {
    const value = addValue.trim()
    if (!isEmail(value)) {
      setAddError('Enter a valid email address, e.g. name@company.com')
      return
    }
    if (
      recipients.some(
        (r: any) => r.recipientEmail.toLowerCase() === value.toLowerCase(),
      )
    ) {
      setAddError('This email is already a recipient for this report.')
      return
    }
    createRecipient(
      {
        partnerId: card.partnerId,
        serviceName: card.serviceName,
        serviceNameDescription: card.serviceNameDescription,
        tag: card.tag,
        recipientEmail: value,
        isActive: true,
      },
      {
        onSuccess: () => {
          setAddValue('')
          setAddError('')
          notify(`Added ${value} to ${card.serviceName}`, 'success')
          refreshAfterChange()
        },
        onError: ({ response: { data: errData } }: any) => {
          setAddError(errData?.message || 'Could not add recipient')
        },
      },
    )
  }

  const handleEditSave = (id: string, newEmail: string) => {
    const value = newEmail.trim()
    if (!isEmail(value)) {
      setEditError('Enter a valid email address, e.g. name@company.com')
      return
    }
    if (
      recipients.some(
        (r: any) =>
          r.id !== id && r.recipientEmail.toLowerCase() === value.toLowerCase(),
      )
    ) {
      setEditError('This email is already a recipient for this report.')
      return
    }
    updateRecipient(
      { id, recipientEmail: value, isActive: true },
      {
        onSuccess: () => {
          resetTransientState()
          notify('Recipient updated', 'success')
          refreshAfterChange()
        },
        onError: ({ response: { data: errData } }: any) => {
          setEditError(errData?.message || 'Could not update recipient')
        },
      },
    )
  }

  const handleConfirmDelete = (id: string, email: string) => {
    deleteRecipient(
      { id },
      {
        onSuccess: () => {
          resetTransientState()
          notify(`Removed ${email} — other reports are unaffected`, 'success')
          refreshAfterChange()
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData?.message || 'Could not remove recipient', 'error')
          setConfirmingDeleteId(null)
        },
      },
    )
  }

  const latestChange = recipients.reduce((latest: any, r: any) => {
    if (!r.updatedOn) return latest
    if (!latest || new Date(r.updatedOn) > new Date(latest.updatedOn)) return r
    return latest
  }, null)

  return (
    <div
      onClick={onClose}
      className='fixed inset-0 bg-black bg-opacity-50 z-[80] flex items-center justify-center p-6'>
      <div
        onClick={(e) => e.stopPropagation()}
        className='bg-white rounded-xl shadow-2xl w-[760px] max-w-[94vw] max-h-[84vh] flex flex-col overflow-hidden'>
        <div className='flex items-start gap-3 px-[22px] pt-[18px] pb-4 border-b border-N-100'>
          <div className='flex-1 flex flex-col gap-[5px] min-w-0'>
            <div className='flex items-center gap-2.5 flex-wrap'>
              <span className='text-[16.5px] font-bold text-N-800'>
                {card.serviceNameDescription}
              </span>
              <ProcessBadge label={card.tag} />
            </div>
            <span className='text-[12.5px] text-N-500'>
              Recipients for <b className='text-N-700'>{card.partnerName}</b> (
              {card.partnerCode}) · {recipients.length}{' '}
              {recipients.length === 1 ? 'recipient' : 'recipients'} · other
              reports are unaffected
            </span>
          </div>
          <button
            type='button'
            title='Close'
            onClick={onClose}
            className='w-8 h-8 flex items-center justify-center bg-N-100 hover:bg-N-200 rounded-md text-N-600'>
            <i className='ri-close-line text-[13px]'></i>
          </button>
        </div>

        <div className='flex-1 overflow-y-auto px-[22px] pt-[10px] pb-3'>
          {isLoading && (
            <div className='text-[13px] text-N-500 py-4'>Loading…</div>
          )}

          {!isLoading && recipients.length > 0 && (
            <>
              <div className='grid grid-cols-[minmax(220px,1fr)_170px_150px_96px] gap-2.5 px-3 py-[9px] border-b border-N-200 text-[11px] font-bold tracking-wide text-N-500'>
                <span>EMAIL ADDRESS</span>
                <span>MODIFIED BY</span>
                <span>MODIFIED ON</span>
                <span className='text-right'>ACTIONS</span>
              </div>
              {recipients.map((recipient: any) => (
                <RecipientRow
                  key={recipient.id}
                  recipient={recipient}
                  canEdit={canEdit}
                  isEditing={editingId === recipient.id}
                  isConfirmingDelete={confirmingDeleteId === recipient.id}
                  editError={editingId === recipient.id ? editError : ''}
                  onStartEdit={() => {
                    setEditingId(recipient.id)
                    setConfirmingDeleteId(null)
                    setEditError('')
                  }}
                  onCancelEdit={() => {
                    setEditingId(null)
                    setEditError('')
                  }}
                  onSaveEdit={(newEmail) =>
                    handleEditSave(recipient.id, newEmail)
                  }
                  onStartDelete={() => {
                    setConfirmingDeleteId(recipient.id)
                    setEditingId(null)
                  }}
                  onCancelDelete={() => setConfirmingDeleteId(null)}
                  onConfirmDelete={() =>
                    handleConfirmDelete(recipient.id, recipient.recipientEmail)
                  }
                />
              ))}
            </>
          )}

          {!isLoading && recipients.length === 0 && (
            <div className='border-[1.5px] border-dashed border-N-200 rounded-md px-[18px] py-[22px] my-2.5 text-center text-[13px] text-N-500'>
              No recipients configured for this report yet.
            </div>
          )}
        </div>

        <div className='border-t border-N-100 px-[22px] pt-3.5 pb-4 flex flex-col gap-2 bg-N-50'>
          {canEdit ? (
            <>
              <div className='flex items-center gap-2 flex-wrap'>
                <input
                  value={addValue}
                  onChange={(e) => {
                    setAddValue(e.target.value)
                    setAddError('')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAdd()
                  }}
                  placeholder='name@company.com'
                  className='flex-1 min-w-[240px] max-w-[340px] px-3 py-[9px] border border-N-300 rounded-md text-[13.5px] outline-none text-N-800 bg-white focus:border-P-500'
                />
                <button
                  type='button'
                  disabled={isCreating}
                  onClick={handleAdd}
                  className='border-none bg-P-500 hover:bg-P-600 disabled:bg-P-300 text-white rounded-md px-4 py-[9px] text-[13px] font-semibold'>
                  Add recipient
                </button>
              </div>
              {addError && (
                <div className='text-R-500 text-[12.5px]'>{addError}</div>
              )}
            </>
          ) : (
            <div className='text-[12.5px] text-P-800'>
              Read-only access — contact an administrator to change recipients.
            </div>
          )}
          <span className='text-xs text-N-400'>
            {latestChange
              ? `Last updated ${dateToReadableString(
                  latestChange.updatedOn,
                )} · ${
                  latestChange.modifiedBy ?? latestChange.createdBy ?? 'Unknown'
                }`
              : 'No changes recorded yet'}
          </span>
        </div>
      </div>
    </div>
  )
}

export default EmailAlertsRecipientsModal
